import imageCompression from 'browser-image-compression';
import { ALLOWED_VIDEO_MIME_TYPES, resolveMimeType } from './mediaConstraints';

/**
 * Únicos formatos que o Rekognition analisa. Precisa espelhar
 * ALLOWED_MIME_TYPES em backend/src/modules/posts/services/
 * GenerateUploadPresignedUrlService.ts.
 */
const REKOGNITION_MIME_TYPES = ['image/jpeg', 'image/png'];

/** Teto do input de imagem — a conversão precisa caber nele. */
const MAX_CONVERTED_SIZE_MB = 9;

/** Fotos de projeto não precisam de mais que isso para o feed. */
const MAX_CONVERTED_DIMENSION = 2560;

/**
 * Arquivo que o pipeline de publicação não sabe processar — formato que o
 * Rekognition não lê e que o navegador não conseguiu converter, ou vídeo em
 * container que a moderação não abre.
 *
 * É deliberadamente diferente de uma reprovação de moderação: o arquivo não
 * foi julgado, apenas não pôde ser lido.
 */
export class UnsupportedFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedFileError';
  }
}

function withJpgExtension(filename: string): string {
  const base = filename.replace(/\.[^./\\]+$/, '');
  return `${base || 'foto'}.jpg`;
}

/**
 * Prepara um arquivo escolhido pelo usuário para o upload direto ao S3.
 *
 * Vídeo sobe intacto: nenhuma análise automática o lê, e a revisão é humana,
 * feita depois que o post já está no ar. Foto em JPEG ou PNG também passa
 * intacta; WEBP, GIF e demais formatos que o navegador decodifica viram JPEG
 * — inclusive o nome do arquivo, porque o backend deriva o content-type da
 * extensão ao assinar a URL.
 *
 * Lança UnsupportedFileError quando não há conversão possível: container de
 * vídeo fora de MP4/MOV, ou HEIC fora do Safari, onde o canvas não abre.
 */
export async function prepareMediaForUpload(file: File): Promise<File> {
  const mimeType = resolveMimeType(file);

  if (mimeType.startsWith('video/')) {
    if (!ALLOWED_VIDEO_MIME_TYPES.includes(mimeType)) {
      throw new UnsupportedFileError(
        'Formato de vídeo não suportado. Envie o vídeo em MP4 ou MOV.',
      );
    }

    // O backend assina a URL com o content-type derivado da extensão. Se o
    // File subir com `type` vazio, o navegador manda o PUT sem Content-Type e
    // o S3 recusa por assinatura — por isso o tipo é reposto aqui.
    return file.type
      ? file
      : new File([file], file.name, {
          type: mimeType,
          lastModified: file.lastModified,
        });
  }

  if (!mimeType.startsWith('image/')) {
    throw new UnsupportedFileError(
      'Este arquivo não é uma imagem. Envie a foto como JPG ou PNG.',
    );
  }

  if (REKOGNITION_MIME_TYPES.includes(mimeType)) {
    return file;
  }

  try {
    const converted = await imageCompression(file, {
      fileType: 'image/jpeg',
      maxSizeMB: MAX_CONVERTED_SIZE_MB,
      maxWidthOrHeight: MAX_CONVERTED_DIMENSION,
      initialQuality: 0.9,
      useWebWorker: true,
    });

    return new File([converted], withJpgExtension(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    throw new UnsupportedFileError(
      'Não conseguimos ler esta imagem. Converta para JPG ou PNG e envie de novo.',
    );
  }
}
